import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { formatRego } from '@/lib/formatters';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Plus, Edit, Trash2, MoreVertical, Car, Search, Loader2, CalendarPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';
import QuickBookingModal from '@/components/QuickBookingModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Australian states for rego lookup
const AU_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'SA', label: 'South Australia' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'NT', label: 'Northern Territory' },
  { value: 'ACT', label: 'Australian Capital Territory' },
];

export default function CustomerDetails() {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const { getSiteParams } = useSite();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  // iter122: quotes + recent invoices for this customer. Surfaces the
  // status='quoted' invoices created via the Booking widget so the SA
  // can follow up later (open the quote, convert it to a booking).
  const [recentInvoices, setRecentInvoices] = useState([]);
  // iter129 — standalone quotes (no booking, no JC) for this customer.
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  // Pre-filled booking modal state — opened from each vehicle row's
  // ⋮ menu so the SM can schedule a booking for the current customer +
  // vehicle in one click.
  const [bookingPrefill, setBookingPrefill] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [selectedState, setSelectedState] = useState('VIC');
  
  // Vehicle lookup data
  const [makeOptions, setMakeOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [variantOptions, setVariantOptions] = useState([]);
  const [loadingMakes, setLoadingMakes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  
  const [formData, setFormData] = useState({
    rego: '',
    make: '',
    model: '',
    year: '',
    variant: '',
    vin: '',
    color: '',
    last_service_date: '',
    odometer: ''
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchCustomer();
      fetchVehicles();
      fetchRecentInvoices();
      fetchRecentQuotes();
    }
  }, [user, customerId]);

  const fetchCustomer = async () => {
    try {
      const siteParams = getSiteParams();
      const response = await fetch(`${BACKEND_URL}/api/customers/${customerId}${siteParams}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomer(data);
      } else {
        toast.error('Customer not found');
        navigate('/customers');
      }
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      toast.error('Failed to load customer');
    }
  };

  const fetchVehicles = async () => {
    try {
      const siteParams = getSiteParams();
      const response = await fetch(`${BACKEND_URL}/api/customers/${customerId}/vehicles${siteParams}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setVehicles(data.vehicles);
      }
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      toast.error('Failed to load vehicles');
    }
  };

  // iter122: fetch quotes + recent invoices for this customer so the
  // Customer Details page surfaces "phone quote" rows the SM created
  // via the Booking widget → Send Quote SMS button. SA can then
  // follow up by opening the quote and either converting it to a
  // booking OR archiving.
  const fetchRecentInvoices = async () => {
    try {
      const siteParams = getSiteParams();
      const sep = siteParams ? '&' : '?';
      const url = `${BACKEND_URL}/api/invoices${siteParams}${sep}customer_id=${customerId}&limit=10`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRecentInvoices(data.invoices || data.items || []);
      }
    } catch (error) {
      console.error('Failed to fetch recent invoices:', error);
    }
  };

  // iter129 — Standalone quotes (no booking) for this customer
  const fetchRecentQuotes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/quotes?customer_id=${customerId}&limit=20`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setRecentQuotes(data.quotes || []);
      }
    } catch (error) {
      console.error('Failed to fetch recent quotes:', error);
    }
  };

  // Vehicle Make/Model/Year/Variant Lookup Functions
  const fetchMakes = async () => {
    setLoadingMakes(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicle-lookup/makes`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMakeOptions(data.makes || []);
      }
    } catch (error) {
      console.error('Failed to fetch makes:', error);
    } finally {
      setLoadingMakes(false);
    }
  };

  const fetchModels = async (make) => {
    if (!make) {
      setModelOptions([]);
      return;
    }
    setLoadingModels(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicle-lookup/models/${encodeURIComponent(make)}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setModelOptions(data.models || []);
      }
    } catch (error) {
      console.error('Failed to fetch models:', error);
    } finally {
      setLoadingModels(false);
    }
  };

  const fetchYearsAndVariants = async (make, model) => {
    if (!make || !model) {
      setYearOptions([]);
      setVariantOptions([]);
      return;
    }
    try {
      const [yearsRes, variantsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/vehicle-lookup/years/${encodeURIComponent(make)}/${encodeURIComponent(model)}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/vehicle-lookup/variants/${encodeURIComponent(make)}/${encodeURIComponent(model)}`, { credentials: 'include' })
      ]);
      
      if (yearsRes.ok) {
        const yearsData = await yearsRes.json();
        setYearOptions(yearsData.years || []);
      }
      if (variantsRes.ok) {
        const variantsData = await variantsRes.json();
        setVariantOptions(variantsData.variants || []);
      }
    } catch (error) {
      console.error('Failed to fetch years/variants:', error);
    }
  };

  // Load makes when dialog opens
  useEffect(() => {
    if (showAddDialog || editingVehicle) {
      fetchMakes();
    }
  }, [showAddDialog, editingVehicle]);

  // Load models when make changes
  useEffect(() => {
    if (formData.make) {
      fetchModels(formData.make);
    } else {
      setModelOptions([]);
      setYearOptions([]);
      setVariantOptions([]);
    }
  }, [formData.make]);

  // Load years/variants when model changes
  useEffect(() => {
    if (formData.make && formData.model) {
      fetchYearsAndVariants(formData.make, formData.model);
    } else {
      setYearOptions([]);
      setVariantOptions([]);
    }
  }, [formData.model]);

  const handleMakeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      make: value,
      model: '',
      year: '',
      variant: ''
    }));
  };

  const handleModelChange = (value) => {
    setFormData(prev => ({
      ...prev,
      model: value,
      year: '',
      variant: ''
    }));
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    
    try {
      const siteParams = getSiteParams();
      // Clean payload - convert empty strings to null for optional fields
      const payload = {
        rego: formData.rego || null,
        make: formData.make || null,
        model: formData.model || null,
        year: formData.year ? parseInt(formData.year) : null,
        variant: formData.variant || null,
        vin: formData.vin || null,
        color: formData.color || null,
        last_service_date: formData.last_service_date || null,
        odometer: formData.odometer ? parseInt(formData.odometer) : null
      };
      
      const response = await fetch(`${BACKEND_URL}/api/customers/${customerId}/vehicles${siteParams}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('Vehicle added successfully');
        setShowAddDialog(false);
        resetForm();
        fetchVehicles();
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to add vehicle' }));
        toast.error(errorData.detail || 'Failed to add vehicle');
      }
    } catch (error) {
      console.error('Failed to add vehicle:', error);
      toast.error('Failed to add vehicle');
    }
  };

  const handleUpdateVehicle = async (e) => {
    e.preventDefault();
    
    try {
      const siteParams = getSiteParams();
      // Clean payload - convert empty strings to null for optional fields
      const payload = {
        rego: formData.rego || null,
        make: formData.make || null,
        model: formData.model || null,
        year: formData.year ? parseInt(formData.year) : null,
        variant: formData.variant || null,
        vin: formData.vin || null,
        color: formData.color || null,
        last_service_date: formData.last_service_date || null,
        odometer: formData.odometer ? parseInt(formData.odometer) : null
      };
      
      const response = await fetch(`${BACKEND_URL}/api/vehicles/${editingVehicle.vehicle_id}${siteParams}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('Vehicle updated successfully');
        setEditingVehicle(null);
        resetForm();
        fetchVehicles();
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to update vehicle' }));
        toast.error(errorData.detail || 'Failed to update vehicle');
      }
    } catch (error) {
      console.error('Failed to update vehicle:', error);
      toast.error('Failed to update vehicle');
    }
  };

  const handleDeleteVehicle = async (vehicleId, rego) => {
    if (!window.confirm(`Delete vehicle "${rego || 'Unknown'}"?`)) {
      return;
    }
    
    try {
      const siteParams = getSiteParams();
      const response = await fetch(`${BACKEND_URL}/api/vehicles/${vehicleId}${siteParams}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Vehicle deleted successfully');
        fetchVehicles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete vehicle');
      }
    } catch (error) {
      console.error('Failed to delete vehicle:', error);
      toast.error('Failed to delete vehicle');
    }
  };

  const resetForm = () => {
    setFormData({
      rego: '',
      make: '',
      model: '',
      year: '',
      variant: '',
      vin: '',
      color: '',
      last_service_date: '',
      odometer: ''
    });
    setSelectedState('VIC');
    setModelOptions([]);
    setYearOptions([]);
    setVariantOptions([]);
  };

  // Australian Rego Lookup function
  const handleRegoLookup = async () => {
    if (!formData.rego || formData.rego.length < 2) {
      toast.error('Please enter a valid registration number');
      return;
    }

    setLookupLoading(true);
    try {
      const lookupUrl = `${BACKEND_URL}/api/vehicles/lookup/rego?rego=${encodeURIComponent(formData.rego)}&state=${selectedState}`;
      console.log('Rego lookup URL:', lookupUrl);
      
      const response = await fetch(lookupUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('Rego lookup response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Rego lookup data:', data);
        
        if (data.found) {
          // Update form with all returned fields
          setFormData(prev => ({
            ...prev,
            make: data.make || prev.make,
            model: data.model || prev.model,
            year: data.year ? data.year.toString() : prev.year,
            variant: data.variant || prev.variant,
            vin: data.vin || prev.vin,
            color: data.color || prev.color
          }));
          
          // Trigger cascading dropdown updates for the returned make/model
          if (data.make) {
            fetchModels(data.make);
            if (data.model) {
              fetchYearsAndVariants(data.make, data.model);
            }
          }
          
          const variantText = data.variant ? ` ${data.variant}` : '';
          toast.success(`Vehicle found: ${data.make} ${data.model}${variantText} (${data.year})`);
        } else {
          toast.info(data.message || 'No vehicle found for this registration. Please enter details manually.');
        }
      } else if (response.status === 401) {
        toast.error('Session expired. Please refresh the page and login again.');
      } else {
        const errorText = await response.text();
        console.error('Rego lookup error response:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          toast.error(errorData.detail || 'Failed to lookup registration');
        } catch {
          toast.error('Failed to lookup registration');
        }
      }
    } catch (error) {
      console.error('Rego lookup error:', error);
      toast.error('Failed to lookup registration. Please check your connection.');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      rego: vehicle.rego || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year ? vehicle.year.toString() : '',
      variant: vehicle.variant || '',
      vin: vehicle.vin || '',
      color: vehicle.color || '',
      last_service_date: vehicle.last_service_date ? vehicle.last_service_date.split('T')[0] : '',
      odometer: vehicle.odometer ? vehicle.odometer.toString() : ''
    });
    // Trigger model/year/variant loading for edit mode
    if (vehicle.make) {
      fetchModels(vehicle.make);
      if (vehicle.model) {
        fetchYearsAndVariants(vehicle.make, vehicle.model);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return null;
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/customers')}
            className="mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Customers
          </Button>
          
          <h1 className="font-['Chivo'] font-bold text-4xl sm:text-5xl text-slate-900 mb-2" data-testid="customer-name">
            {customer.display_name}
          </h1>
          <div className="flex flex-wrap gap-4 text-slate-600">
            {customer.email && <span data-testid="customer-email">{customer.email}</span>}
            {customer.phone && <span data-testid="customer-phone">{customer.phone}</span>}
            {customer.address && <span data-testid="customer-address">{customer.address}</span>}
          </div>
        </div>

        {/* Customer Info Card */}
        <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 mb-8" data-testid="customer-info-card">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">First Name</p>
              <p className="text-slate-900" data-testid="customer-first-name">{customer.first_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Last Name</p>
              <p className="text-slate-900" data-testid="customer-last-name">{customer.last_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Email</p>
              <p className="text-slate-900" data-testid="customer-email-detail">{customer.email || '-'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Phone</p>
              <p className="text-slate-900" data-testid="customer-phone-detail">{customer.phone || '-'}</p>
            </div>
          </div>
        </Card>

        {/* iter122: Recent Quotes & Invoices — surfaces every doc the
            customer has (quoted, sent, paid) so the SA can follow up on
            phone quotes without leaving the customer page. Quote-status
            rows get a sky-blue pill so they jump out from billed invoices. */}
        {recentInvoices.length > 0 && (
          <Card className="mb-8 p-6" data-testid="customer-invoices-card">
            <h2 className="font-['Chivo'] font-bold text-2xl text-slate-900 mb-4">
              Quotes &amp; Invoices ({recentInvoices.length})
            </h2>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Doc #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentInvoices.map((inv) => {
                    const status = inv.status || 'draft';
                    const statusStyles = {
                      quoted: 'bg-sky-100 text-sky-800 border-sky-200',
                      sent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
                      paid: 'bg-emerald-200 text-emerald-900 border-emerald-300',
                      draft: 'bg-slate-100 text-slate-700 border-slate-200',
                      overdue: 'bg-rose-100 text-rose-800 border-rose-200',
                    }[status] || 'bg-slate-100 text-slate-700 border-slate-200';
                    const dateStr = inv.invoice_date || inv.issue_date || inv.created_at || '';
                    const dateDisplay = dateStr ? new Date(dateStr).toLocaleDateString() : '-';
                    return (
                      <TableRow
                        key={inv.invoice_id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(`/invoices/${inv.invoice_id}/edit`)}
                        data-testid={`customer-invoice-row-${inv.invoice_id}`}
                      >
                        <TableCell className="font-mono font-medium">{inv.doc_no}</TableCell>
                        <TableCell>{dateDisplay}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusStyles}`}>
                            {status.toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-600">{inv.vehicle_rego || '-'}</TableCell>
                        <TableCell>
                          {inv.source === 'quote_widget' ? (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-indigo-100 text-indigo-700">
                              Phone quote
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          ${(inv.total || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/invoices/${inv.invoice_id}/edit`);
                            }}
                            data-testid={`customer-invoice-open-${inv.invoice_id}`}
                          >
                            Open
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <p className="text-[11px] text-slate-500 mt-3 italic">
              Tip: rows tagged <span className="font-mono text-[10px] px-1 py-0.5 bg-indigo-50 rounded border border-indigo-200">Phone quote</span> were created via the Booking widget → Send Quote SMS. Click any row to open, then convert to a booking when the customer confirms.
            </p>
          </Card>
        )}

        {/* iter129 — Standalone Phone Quotes (no booking, no JC). Surfaced
            separately from the legacy quote-via-invoice rows above. */}
        {recentQuotes.length > 0 && (
          <Card className="mb-8 p-6" data-testid="customer-quotes-card">
            <h2 className="font-['Chivo'] font-bold text-2xl text-slate-900 mb-4">
              Phone Quotes ({recentQuotes.length})
            </h2>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Quote #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead className="text-center">SMS sent</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentQuotes.map((q) => {
                    const tone = {
                      draft: 'bg-slate-100 text-slate-800 border-slate-200',
                      sent: 'bg-blue-100 text-blue-800 border-blue-200',
                      accepted: 'bg-emerald-100 text-emerald-800 border-emerald-200',
                      declined: 'bg-rose-100 text-rose-800 border-rose-200',
                      expired: 'bg-amber-100 text-amber-800 border-amber-200',
                    }[q.status] || 'bg-slate-100 text-slate-700 border-slate-200';
                    const dateDisplay = q.created_at ? new Date(q.created_at).toLocaleDateString() : '-';
                    return (
                      <TableRow
                        key={q.quote_id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(`/quotes/${q.quote_id}`)}
                        data-testid={`customer-quote-row-${q.quote_id}`}
                      >
                        <TableCell className="font-mono font-medium text-indigo-700">{q.quote_number}</TableCell>
                        <TableCell>{dateDisplay}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${tone}`}>
                            {(q.status || '').toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-600">{q.vehicle_rego || '—'}</TableCell>
                        <TableCell className="text-center">
                          {q.sms_sent_count > 0 ? (
                            <span className="text-indigo-700 font-semibold">{q.sms_sent_count}</span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          ${(q.total || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/quotes/${q.quote_id}`);
                            }}
                            data-testid={`customer-quote-open-${q.quote_id}`}
                          >
                            Open
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <p className="text-[11px] text-slate-500 mt-3 italic">
              Phone quotes carry no booking and no job card. Click any row to see all line items, send a follow-up SMS, or mark as accepted/declined.
            </p>
          </Card>
        )}

        <div className="mb-8 flex items-center justify-between">
          <h2 className="font-['Chivo'] font-bold text-2xl text-slate-900">
            Vehicles ({vehicles.length})
          </h2>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="h-12 px-6 bg-blue-600 hover:bg-blue-700" data-testid="add-vehicle-button">
                <Plus className="h-5 w-5 mr-2" />
                Add Vehicle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Vehicle</DialogTitle>
                <DialogDescription>
                  All fields are optional. Add vehicle details and loan car information.
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleAddVehicle} className="space-y-4">
                {/* Rego Lookup Section */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                    Australian Rego Lookup
                  </Label>
                  <p className="text-xs text-slate-500 mb-3">
                    Enter the registration number and state to auto-fill vehicle details
                  </p>
                  <div className="flex gap-2">
                    <Input
                      id="rego"
                      value={formData.rego}
                      onChange={(e) => setFormData({...formData, rego: formatRego(e.target.value)})}
                      className="h-12 flex-1"
                      placeholder="e.g., ABC 123"
                      data-testid="vehicle-rego-input"
                    />
                    <Select value={selectedState} onValueChange={setSelectedState}>
                      <SelectTrigger className="w-32 h-12" data-testid="state-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AU_STATES.map(state => (
                          <SelectItem key={state.value} value={state.value}>
                            {state.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      onClick={handleRegoLookup}
                      disabled={lookupLoading || !formData.rego}
                      className="h-12 px-4 bg-slate-700 hover:bg-slate-800"
                      data-testid="rego-lookup-button"
                    >
                      {lookupLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-2" />
                          Lookup
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="color">Color</Label>
                    <Input
                      id="color"
                      value={formData.color}
                      onChange={(e) => setFormData({...formData, color: e.target.value})}
                      className="h-12"
                      data-testid="vehicle-color-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="make">Make</Label>
                    <SearchableSelect
                      options={makeOptions}
                      value={formData.make}
                      onValueChange={handleMakeChange}
                      placeholder={loadingMakes ? "Loading..." : "Select make"}
                      searchPlaceholder="Search makes..."
                      emptyMessage="No makes found"
                      data-testid="vehicle-make-select"
                    />
                  </div>
                  <div>
                    <Label htmlFor="model">Model</Label>
                    <SearchableSelect
                      options={modelOptions}
                      value={formData.model}
                      onValueChange={handleModelChange}
                      disabled={!formData.make}
                      placeholder={loadingModels ? "Loading..." : (formData.make ? "Select model" : "Select make first")}
                      searchPlaceholder="Search models..."
                      emptyMessage="No models found"
                      data-testid="vehicle-model-select"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="year">Year</Label>
                    <SearchableSelect
                      options={yearOptions.map(y => y.toString())}
                      value={formData.year}
                      onValueChange={(value) => setFormData({...formData, year: value})}
                      disabled={!formData.model}
                      placeholder={formData.model ? "Select year" : "Select model first"}
                      searchPlaceholder="Search years..."
                      emptyMessage="No years found"
                      data-testid="vehicle-year-select"
                    />
                  </div>
                  <div>
                    <Label htmlFor="variant">Variant</Label>
                    <SearchableSelect
                      options={variantOptions}
                      value={formData.variant}
                      onValueChange={(value) => setFormData({...formData, variant: value})}
                      disabled={!formData.model}
                      placeholder={formData.model ? "Select variant" : "Select model first"}
                      searchPlaceholder="Search variants..."
                      emptyMessage="No variants found"
                      data-testid="vehicle-variant-select"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="vin">VIN</Label>
                  <Input
                    id="vin"
                    value={formData.vin}
                    onChange={(e) => setFormData({...formData, vin: e.target.value})}
                    className="h-12"
                    placeholder="Vehicle Identification Number"
                    data-testid="vehicle-vin-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="last_service_date">Last Serviced Date</Label>
                    <Input
                      id="last_service_date"
                      type="date"
                      value={formData.last_service_date}
                      onChange={(e) => setFormData({...formData, last_service_date: e.target.value})}
                      className="h-12"
                      data-testid="vehicle-last-service-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="odometer">Odometer (km)</Label>
                    <Input
                      id="odometer"
                      type="number"
                      value={formData.odometer}
                      onChange={(e) => setFormData({...formData, odometer: e.target.value})}
                      className="h-12"
                      placeholder="e.g., 85000"
                      data-testid="vehicle-odometer-input"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 h-12 bg-blue-600 hover:bg-blue-700" data-testid="save-vehicle-button">
                    Save Vehicle
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }} className="flex-1 h-12" data-testid="cancel-add-vehicle-button">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Registration</TableHead>
                <TableHead>Make & Model</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Last Serviced</TableHead>
                <TableHead>Odometer</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Car className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No vehicles found</p>
                    <p className="text-slate-400 text-sm mt-1">Add a vehicle to get started</p>
                  </TableCell>
                </TableRow>
              ) : (
                vehicles.map((vehicle) => (
                  <TableRow 
                    key={vehicle.vehicle_id} 
                    data-testid={`vehicle-row-${vehicle.vehicle_id}`} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/vehicles/${vehicle.vehicle_id}`)}
                  >
                    <TableCell className="font-medium text-blue-600 hover:text-blue-800 hover:underline" data-testid="vehicle-rego">{vehicle.rego || '-'}</TableCell>
                    <TableCell className="text-slate-600" data-testid="vehicle-make-model">
                      {vehicle.make && vehicle.model ? `${vehicle.make} ${vehicle.model}` : (vehicle.make || vehicle.model || '-')}
                    </TableCell>
                    <TableCell className="text-slate-600" data-testid="vehicle-year">{vehicle.year || '-'}</TableCell>
                    <TableCell className="text-slate-600" data-testid="vehicle-last-service">
                      {vehicle.last_service_date ? new Date(vehicle.last_service_date).toLocaleDateString('en-AU') : '-'}
                    </TableCell>
                    <TableCell className="text-slate-600" data-testid="vehicle-odometer">
                      {vehicle.odometer ? `${vehicle.odometer.toLocaleString()} km` : '-'}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(vehicle); }} data-testid="edit-vehicle-action">
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              // Pre-fill the QuickBooking modal with this
                              // customer + vehicle. The booking POST will
                              // persist `customer_id` + `vehicle_id` so
                              // when the booking is converted to a JC the
                              // customer + vehicle records flow through
                              // intact (no manual re-link needed).
                              setBookingPrefill({
                                customer_id: customer.customer_id,
                                customer_name: customer.display_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
                                customer_phone: customer.mobile || customer.phone || '',
                                customer_email: customer.email || '',
                                vehicle_id: vehicle.vehicle_id,
                                vehicle_rego: vehicle.rego,
                                vehicle_make: vehicle.make,
                                vehicle_model: vehicle.model,
                                vehicle_year: vehicle.year,
                              });
                            }}
                            data-testid={`vehicle-create-booking-${vehicle.vehicle_id}`}
                            className="text-emerald-700"
                          >
                            <CalendarPlus className="h-4 w-4 mr-2" />
                            Create Booking
                          </DropdownMenuItem>
                          {user && user.role === 'super_admin' && (
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); handleDeleteVehicle(vehicle.vehicle_id, vehicle.rego); }}
                              className="text-red-600"
                              data-testid="delete-vehicle-action"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      {editingVehicle && (
        <Dialog open={!!editingVehicle} onOpenChange={() => { setEditingVehicle(null); resetForm(); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Vehicle</DialogTitle>
              <DialogDescription>
                Update vehicle information
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleUpdateVehicle} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_rego">Registration (Rego)</Label>
                  <Input
                    id="edit_rego"
                    value={formData.rego}
                    onChange={(e) => setFormData({...formData, rego: formatRego(e.target.value)})}
                    className="h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_color">Color</Label>
                  <Input
                    id="edit_color"
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    className="h-12"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_make">Make</Label>
                  <Select 
                    value={formData.make} 
                    onValueChange={handleMakeChange}
                  >
                    <SelectTrigger className="h-12" data-testid="edit-vehicle-make-select">
                      <SelectValue placeholder={loadingMakes ? "Loading..." : "Select make"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {makeOptions.map((make) => (
                        <SelectItem key={make} value={make}>{make}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit_model">Model</Label>
                  <Select 
                    value={formData.model} 
                    onValueChange={handleModelChange}
                    disabled={!formData.make}
                  >
                    <SelectTrigger className="h-12" data-testid="edit-vehicle-model-select">
                      <SelectValue placeholder={loadingModels ? "Loading..." : (formData.make ? "Select model" : "Select make first")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {modelOptions.map((model) => (
                        <SelectItem key={model} value={model}>{model}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_year">Year</Label>
                  <Select 
                    value={formData.year} 
                    onValueChange={(value) => setFormData({...formData, year: value})}
                    disabled={!formData.model}
                  >
                    <SelectTrigger className="h-12" data-testid="edit-vehicle-year-select">
                      <SelectValue placeholder={formData.model ? "Select year" : "Select model first"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {yearOptions.map((year) => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit_variant">Variant</Label>
                  <Select 
                    value={formData.variant} 
                    onValueChange={(value) => setFormData({...formData, variant: value})}
                    disabled={!formData.model}
                  >
                    <SelectTrigger className="h-12" data-testid="edit-vehicle-variant-select">
                      <SelectValue placeholder={formData.model ? "Select variant" : "Select model first"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {variantOptions.map((variant) => (
                        <SelectItem key={variant} value={variant}>{variant}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="edit_vin">VIN</Label>
                <Input
                  id="edit_vin"
                  value={formData.vin}
                  onChange={(e) => setFormData({...formData, vin: e.target.value})}
                  className="h-12"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_last_service_date">Last Serviced Date</Label>
                  <Input
                    id="edit_last_service_date"
                    type="date"
                    value={formData.last_service_date}
                    onChange={(e) => setFormData({...formData, last_service_date: e.target.value})}
                    className="h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_odometer">Odometer (km)</Label>
                  <Input
                    id="edit_odometer"
                    type="number"
                    value={formData.odometer}
                    onChange={(e) => setFormData({...formData, odometer: e.target.value})}
                    className="h-12"
                    placeholder="e.g., 85000"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 h-12 bg-blue-600 hover:bg-blue-700" data-testid="update-vehicle-button">
                  Update Vehicle
                </Button>
                <Button type="button" variant="outline" onClick={() => { setEditingVehicle(null); resetForm(); }} className="flex-1 h-12" data-testid="cancel-edit-vehicle-button">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Pre-filled booking creation popup — fired from the vehicle ⋮ menu.
          Mounted at page level so the modal lifecycle survives unmounting
          the menu. Both customer + vehicle ids land on the booking so the
          link survives all the way through to the job card. */}
      <QuickBookingModal
        open={!!bookingPrefill}
        prefill={bookingPrefill}
        onClose={() => setBookingPrefill(null)}
        onCreated={() => {
          setBookingPrefill(null);
          toast.success('Booking created — view it in the Bookings page');
        }}
        getSiteParams={getSiteParams}
      />
    </Layout>
  );
}
